import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { ShieldCheck } from "lucide-react";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/terms-and-conditions")({
  head: () => ({
    meta: [
      { title: "Terms and Conditions — Healic" },
      {
        name: "description",
        content:
          "Read Healic's Terms and Conditions to understand the rules and guidelines for using our website, apps, and healthcare services.",
      },
      { property: "og:title", content: "Terms and Conditions — Healic" },
      {
        property: "og:description",
        content:
          "Terms of use for Healic's website, apps, and healthcare services.",
      },
      { property: "og:url", content: `${SITE_URL}/terms-and-conditions` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/terms-and-conditions` }],
  }),
  component: TermsPage,
});

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    content: [
      "By accessing or using Healic's website, mobile applications, or any of our services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.",
      "These terms apply to all visitors, users, patients, doctors, staff, and others who access or use Healic's digital platforms and healthcare services.",
    ],
  },
  {
    id: "services",
    title: "Healthcare Services",
    content: [
      "Healic provides planned surgical procedures, diagnostic services, consultations, pharmacy, path lab, and related healthcare services through our network of hospitals and partner facilities.",
      "All medical services are provided by qualified and licensed healthcare professionals. Healic facilitates the coordination of these services but does not directly provide medical treatment unless specified otherwise.",
      "Treatment outcomes, recovery times, and results may vary from patient to patient. Healic does not guarantee specific medical outcomes.",
    ],
  },
  {
    id: "eligibility",
    title: "User Eligibility",
    content: [
      "By using our services, you represent that:",
    ],
    bullets: [
      "You are at least 18 years of age or accessing services under the supervision of a parent or guardian",
      "You have the legal capacity to enter into binding agreements",
      "All information you provide is accurate, complete, and current",
      "You will use our services only for lawful purposes and in accordance with these terms",
    ],
  },
  {
    id: "appointments",
    title: "Appointments and Bookings",
    content: [
      "Appointments can be booked through our website, Customer App, or by contacting our helpline. Booking confirmation is subject to doctor availability and hospital capacity.",
      "We reserve the right to reschedule or cancel appointments due to unforeseen circumstances, including but not limited to doctor unavailability, emergency procedures, or operational requirements.",
      "Patients are required to arrive at the scheduled time. Repeated no-shows may result in restrictions on future bookings.",
    ],
  },
  {
    id: "payments",
    title: "Payments and Pricing",
    content: [
      "Healic offers fixed-price packages for many surgical procedures. Prices are transparent and inclusive of specified services as detailed in the package description.",
      "Additional costs may apply for complications, extended stays, additional diagnostics, or procedures not covered under the selected package.",
      "Payments must be made as per the agreed schedule. We accept multiple payment methods including cash, card, UPI, and digital wallets.",
      "All fees and charges are subject to applicable taxes as per government regulations.",
    ],
  },
  {
    id: "cancellations",
    title: "Cancellations and Refunds",
    content: [
      "Cancellation and refund policies vary by service type:",
    ],
    bullets: [
      "Surgery packages: Cancellation at least 48 hours before the scheduled date qualifies for a full refund minus any non-refundable deposits",
      "Diagnostics and lab tests: Cancellation before sample collection or procedure qualifies for a full refund",
      "OPD consultations: Cancellation at least 24 hours in advance qualifies for a refund or rescheduling",
      "No refunds for no-shows without prior notice",
    ],
    afterBullets: [
      "Refund processing time is typically 7-14 business days from the date of approval.",
    ],
  },
  {
    id: "medical-records",
    title: "Medical Records and Privacy",
    content: [
      "All patient information and medical records are handled in accordance with our Privacy Policy and applicable healthcare data protection laws.",
      "Patients have the right to access their medical records subject to verification and applicable legal requirements.",
      "Medical records may be shared with authorized healthcare providers as necessary for continuity of care.",
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    content: [
      "All content on Healic's website and apps, including text, graphics, logos, images, videos, and software, is the property of Healic or its licensors and is protected by copyright and other intellectual property laws.",
      "Users may not reproduce, distribute, modify, or create derivative works from any content without prior written permission from Healic.",
    ],
  },
  {
    id: "disclaimer",
    title: "Disclaimer of Warranties",
    content: [
      "Healic's services are provided on an 'as is' and 'as available' basis. While we strive for accuracy and reliability, we do not warrant that:",
    ],
    bullets: [
      "The services will be uninterrupted, timely, secure, or error-free",
      "The results obtained from using the services will be accurate or reliable",
      "Any errors in the service will be corrected",
    ],
    afterBullets: [
      "Medical information provided on our platforms is for general informational purposes only and does not constitute professional medical advice. Always consult a qualified healthcare provider for personal medical concerns.",
    ],
  },
  {
    id: "limitation",
    title: "Limitation of Liability",
    content: [
      "To the maximum extent permitted by law, Healic shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our services.",
      "Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.",
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law and Jurisdiction",
    content: [
      "These Terms and Conditions shall be governed by and construed in accordance with the laws of India.",
      "Any disputes arising out of or relating to these terms shall be subject to the exclusive jurisdiction of the courts in Gurugram, Haryana, India.",
    ],
  },
  {
    id: "changes",
    title: "Changes to Terms",
    content: [
      "Healic reserves the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on this page.",
      "Continued use of our services after any changes constitutes acceptance of the revised terms.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    content: [
      "If you have any questions about these Terms and Conditions, please contact us at: support@healic.in",
    ],
  },
];

function TermsPage() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" />
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Legal
            </p>
          </div>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
            Terms and Conditions
          </h1>
          <p className="mt-3 max-w-3xl text-sm opacity-85 sm:text-base">
            Please read these terms carefully before using Healic's website,
            apps, or healthcare services.
          </p>
          <p className="mt-4 text-xs opacity-90">
            Last Updated: June 13, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-4xl px-4 py-10 lg:px-8 lg:py-14">
        <div className="space-y-10">
          {sections.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-soft lg:p-8"
            >
              <h2 className="font-display text-xl font-bold sm:text-2xl">
                {s.title}
              </h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                {s.content.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-foreground/85">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
              {s.afterBullets && (
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
                  {s.afterBullets.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
